import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { Input } from "../../../../../components/ui/uikit/input";
import { PHONE_NUMBER_DIGITS } from "../constants";

// ---------------------------------------------------------------------------
// PhoneNumberInput
//
// A controlled 3-segment split phone input that stores and returns a single
// canonical digits-only string (0–10 digits, no separators). The three
// segments (area / exchange / line) are displayed as separate Input boxes
// separated by a visible "-" dash.
//
// Auto-advances focus from area → exchange when 3 digits entered, and from
// exchange → line when 3 digits entered.
// ---------------------------------------------------------------------------

interface PhoneNumberInputProperties {
  /** Canonical digits string. May arrive formatted; non-digits are stripped internally. */
  value: string;
  onChange: (digits: string) => void;
  onBlur?: () => void;
  /** Base accessible name, e.g. "Primary Phone". Each segment gets a suffix appended. */
  ariaLabel: string;
  required?: boolean;
  /** When true, aria-invalid is set on each segment. */
  invalid?: boolean;
  disabled?: boolean;
  /** Used to derive stable input ids: ${idBase}-area / -exchange / -line. */
  idBase: string;
}

function buildDigits(area: string, exchange: string, line: string): string {
  return (area + exchange + line).slice(0, PHONE_NUMBER_DIGITS);
}

export function PhoneNumberInput({
  value,
  onChange,
  onBlur,
  ariaLabel,
  required,
  invalid,
  disabled,
  idBase,
}: PhoneNumberInputProperties) {
  const { t } = useTranslation();
  const digits = value.replaceAll(/\D/g, "").slice(0, PHONE_NUMBER_DIGITS);
  const area = digits.slice(0, 3);
  const exchange = digits.slice(3, 6);
  const line = digits.slice(6, 10);

  const exchangeReference = useRef<HTMLInputElement>(null);
  const lineReference = useRef<HTMLInputElement>(null);

  function handleAreaChange(event: React.ChangeEvent<HTMLInputElement>) {
    const raw = event.target.value.replaceAll(/\D/g, "").slice(0, 3);
    onChange(buildDigits(raw, exchange, line));
    if (raw.length === 3) {
      exchangeReference.current?.focus();
    }
  }

  function handleExchangeChange(event: React.ChangeEvent<HTMLInputElement>) {
    const raw = event.target.value.replaceAll(/\D/g, "").slice(0, 3);
    onChange(buildDigits(area, raw, line));
    if (raw.length === 3) {
      lineReference.current?.focus();
    }
  }

  function handleLineChange(event: React.ChangeEvent<HTMLInputElement>) {
    const raw = event.target.value.replaceAll(/\D/g, "").slice(0, 4);
    onChange(buildDigits(area, exchange, raw));
  }

  const ariaInvalid = invalid || undefined;
  const sharedProperties = {
    type: "text" as const,
    inputMode: "numeric" as const,
    disabled,
    "aria-required": required,
    "aria-invalid": ariaInvalid,
    onBlur,
  };

  return (
    <div className="flex items-center gap-1">
      <Input
        {...sharedProperties}
        id={`${idBase}-area`}
        value={area}
        onChange={handleAreaChange}
        maxLength={3}
        placeholder={t("review.customer.numberPlaceholder.three")}
        className="w-12"
        aria-label={`${ariaLabel} area code`}
      />
      <span className="text-muted-foreground w-2 shrink-0 select-none text-center text-xs" aria-hidden="true">
        -
      </span>
      <Input
        {...sharedProperties}
        id={`${idBase}-exchange`}
        ref={exchangeReference}
        value={exchange}
        onChange={handleExchangeChange}
        maxLength={3}
        placeholder={t("review.customer.numberPlaceholder.three")}
        className="w-12"
        aria-label={`${ariaLabel} exchange`}
      />
      <span className="text-muted-foreground w-2 shrink-0 select-none text-center text-xs" aria-hidden="true">
        -
      </span>
      <Input
        {...sharedProperties}
        id={`${idBase}-line`}
        ref={lineReference}
        value={line}
        onChange={handleLineChange}
        maxLength={4}
        placeholder={t("review.customer.numberPlaceholder.four")}
        className="w-14"
        aria-label={`${ariaLabel} line number`}
      />
    </div>
  );
}
